import React, {useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import Button from '@adsk/bim360-matrix-react-button';
import RadioButton from '@adsk/bim360-matrix-react-radio-button';
import SelectBox from '@adsk/bim360-matrix-react-select-box';
import SimpleModal from '@adsk/bim360-matrix-react-simple-modal';
import Text from '@adsk/bim360-matrix-react-text';
import i18n from '../../utils/i18n';
import {TEMPLATE_CATEGORY_FIELD_NAMES} from '../../constants/issue-templates';
import {templateCategoryType} from '../../constants/proptype-validation';

const RADIO_BUTTON_VALUES = {
  MOVE: 'move',
  DELETE: 'delete'
};

const TemplateCategoryDeleteModal = ({
  isOpen,
  category,
  templateCategories,
  uncategorizedCategory,
  onConfirm,
  onExited,
  container
}) => {
  const [selectedOption, setSelectedOption] = useState(RADIO_BUTTON_VALUES.MOVE);
  const [selectedOtherCategory, setSelectedOtherCategory] = useState(uncategorizedCategory);
  const options = useMemo(
    () =>
      category
        ? [
          ...templateCategories.filter(
            ({[TEMPLATE_CATEGORY_FIELD_NAMES.ID]: id}) => id !== category[TEMPLATE_CATEGORY_FIELD_NAMES.ID]
          ),
          uncategorizedCategory
        ]
        : [],
    [category, templateCategories, uncategorizedCategory]
  );
  const templatesTarget =
    selectedOption === RADIO_BUTTON_VALUES.MOVE
      ? selectedOtherCategory && selectedOtherCategory.id
      : RADIO_BUTTON_VALUES.DELETE;

  return (
    <SimpleModal
      show={isOpen}
      onExited={() => {
        onExited();
        setSelectedOption(RADIO_BUTTON_VALUES.MOVE);
        setSelectedOtherCategory(uncategorizedCategory);
      }}
      title={i18n.t('deleteTemplateCategory.modalTitle')}
      className="TemplateCategoryDeleteModal"
      footer={<div className="TemplateCategoryDeleteModal__footer">
        <Button styleType={Button.StyleType.CANCEL} onClick={onExited}>
          {i18n.t('navigation.cancel')}
        </Button>
        <Button
          data-testid="TemplateCategoryDeleteModal__delete-button"
          styleType={Button.StyleType.DANGER}
          onClick={() => onConfirm(templatesTarget)}
        >
          {i18n.t('navigation.delete')}
        </Button>
      </div>}
      container={container}
    >
      <Text>{i18n.t('deleteTemplateCategory.modalText')}</Text>
      <div className="TemplateCategoryDeleteModal__options">
        <RadioButton
          className="TemplateCategoryDeleteModal__option-move"
          label={<React.Fragment>
            <Text>{i18n.t('deleteTemplateCategory.moveTemplates')}</Text>
            <SelectBox
              className="TemplateCategoryDeleteModal__option-move-category"
              options={options}
              valueKey={TEMPLATE_CATEGORY_FIELD_NAMES.ID}
              labelKey={TEMPLATE_CATEGORY_FIELD_NAMES.TITLE}
              value={selectedOtherCategory}
              onChange={otherCategory => setSelectedOtherCategory(otherCategory)}
              placeholder={i18n.t('templateAttributes.category.uncategorized')}
              disabled={selectedOption !== RADIO_BUTTON_VALUES.MOVE}
            />
          </React.Fragment>}
          checked={selectedOption === RADIO_BUTTON_VALUES.MOVE}
          onChange={() => setSelectedOption(RADIO_BUTTON_VALUES.MOVE)}
        />
        <RadioButton
          label={i18n.t('deleteTemplateCategory.deleteTemplates')}
          value={RADIO_BUTTON_VALUES.DELETE}
          checked={selectedOption === RADIO_BUTTON_VALUES.DELETE}
          onChange={() => setSelectedOption(RADIO_BUTTON_VALUES.DELETE)}
        />
      </div>
    </SimpleModal>
  );
};

TemplateCategoryDeleteModal.displayName = 'TemplateCategoryDeleteModal';

TemplateCategoryDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  category: templateCategoryType.isRequired,
  templateCategories: PropTypes.arrayOf(templateCategoryType).isRequired,
  uncategorizedCategory: templateCategoryType.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onExited: PropTypes.func.isRequired,
  container: PropTypes.object
};

export default TemplateCategoryDeleteModal;
